import { create } from 'zustand';
import { login as apiLogin, logout as apiLogout, getMe } from '@/api/auth';
import { getApiUrl, ensureCurrentMediaSession, isCurrentAuthSession } from '@/api/client';
import { invalidateMediaSession, revokeMediaSession } from '@/api/media-session';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
  isTokenStorageKey,
} from '@/lib/tokenStorage';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  // Phase 4: auth-api 의 /v1/auth/me 는 role_id 를 돌려주지 않음 (cctv-local
  // role 은 별도 endpoint 로 조회 필요). 호환을 위해 optional 로 완화.
  role_id?: string;
  role_name?: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  generation: number;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: getAccessToken(),
  refreshToken: getRefreshToken(),
  user: null,
  isAuthenticated: !!getAccessToken(),
  isLoading: false,
  error: null,
  generation: 0,

  login: async (email, password) => {
    const generation = get().generation + 1;
    const revokingMedia = revokeMediaSession(getApiUrl());
    clearTokens();
    set({ isLoading: true, error: null, generation, token: null, refreshToken: null,
      user: null, isAuthenticated: false });
    try {
      await revokingMedia.catch(() => {});
      if (get().generation !== generation || getAccessToken()) return;
      const res = await apiLogin(email, password);
      if (get().generation !== generation || getAccessToken()) return;
      setTokens(res.access_token, res.refresh_token);
      await ensureCurrentMediaSession().catch(() => {});
      if (get().generation !== generation || !isCurrentAuthSession(res.access_token, res.refresh_token)) return;
      set({
        token: res.access_token,
        refreshToken: res.refresh_token,
        isAuthenticated: true,
        isLoading: false,
      });
      // Fetch user info
      try {
        const identityToken = getAccessToken();
        const identityRefresh = getRefreshToken();
        const userMe = await getMe();
        if (get().generation !== generation || !isCurrentAuthSession(identityToken, identityRefresh)) return;
        set({ user: { ...userMe, role_name: userMe.role?.name },
          token: getAccessToken(), refreshToken: getRefreshToken() });
      } catch {
        // Non-critical: user info fetch failed
      }
    } catch (err) {
      if (get().generation !== generation || getAccessToken()) return;
      void revokeMediaSession(getApiUrl()).catch(() => {});
      clearTokens();
      const message = err instanceof Error ? err.message : '로그인에 실패했습니다.';
      set({ isLoading: false, error: message, token: null, refreshToken: null,
        user: null, isAuthenticated: false });
      throw err;
    }
  },

  logout: async () => {
    if (!isCurrentAuthSession(get().token, get().refreshToken)) {
      invalidateMediaSession();
      set(s => ({ generation: s.generation + 1, token: null, refreshToken: null,
        user: null, isAuthenticated: false, isLoading: false, error: null }));
      return;
    }
    const mediaRequest = revokeMediaSession(getApiUrl()).catch(() => {});
    const logoutRequest = apiLogout().catch(() => {});
    clearTokens();
    set((s) => ({
      generation: s.generation + 1,
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      error: null,
      isLoading: false,
    }));
    await Promise.all([logoutRequest, mediaRequest]);
  },

  restoreSession: async () => {
    const generation = get().generation;
    const token = getAccessToken();
    const refreshToken = getRefreshToken();
    if (!token) {
      set({ isAuthenticated: false });
      return;
    }
    try {
      const userMe = await getMe();
      if (get().generation !== generation || !getAccessToken() || !isCurrentAuthSession(token, refreshToken)) return;
      const identityToken = getAccessToken();
      const identityRefresh = getRefreshToken();
      await ensureCurrentMediaSession().catch(() => {});
      const currentToken = getAccessToken();
      if (get().generation !== generation || !currentToken || !isCurrentAuthSession(identityToken, identityRefresh)) return;
      set({ user: { ...userMe, role_name: userMe.role?.name }, isAuthenticated: true,
        token: currentToken, refreshToken: getRefreshToken() });
    } catch {
      if (get().generation !== generation || !isCurrentAuthSession(token, refreshToken)) return;
      // Token expired or invalid
      get().clearAuth();
    }
  },

  setTokens: (accessToken, refreshToken) => {
    void revokeMediaSession(getApiUrl()).catch(() => {});
    setTokens(accessToken, refreshToken);
    set((s) => ({ token: accessToken, refreshToken, isAuthenticated: true, generation: s.generation + 1 }));
  },

  clearAuth: () => {
    void revokeMediaSession(getApiUrl()).catch(() => {});
    clearTokens();
    set((s) => ({
      generation: s.generation + 1,
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
    }));
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (!isTokenStorageKey(event.key)) return;
    const state = useAuthStore.getState();
    if (getAccessToken() === state.token && getRefreshToken() === state.refreshToken) return;
    invalidateMediaSession();
    // Another tab owns the new shared credentials; this tab only discards its old identity.
    useAuthStore.setState({ generation: state.generation + 1, token: null, refreshToken: null,
      user: null, isAuthenticated: false, isLoading: false,
      error: '다른 탭에서 로그인 정보가 변경되었습니다. 페이지를 새로고침해 주세요.' });
  });
}
